import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { catchError, finalize, map, Observable, startWith, take, tap } from 'rxjs';
import { ModalComponent, ModalData } from 'shared/src/components/modal/modal.component';
import { ApiService } from 'shared/src/services/api.service';
import { AxonService } from 'shared/src/services/axon.service';
import { LoadingService } from 'shared/src/services/loading.service';
import { NewsbreakService } from 'shared/src/services/newsbreak.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { PROJECT_NAME, ProjectName, ProjectNames } from 'shared/src/tokens';
import { AnalyticsService } from '../../../../../shared/src/services/analytics.service';
import { fadeAnimation } from '../../animations/route-animations';
import { highlightText } from '../../data/quiz';
import { FunnelFlowService } from '../../services/funnel-flow.service';

const LOCAL_PART_REGEX = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
const DOMAIN_LABEL_REGEX = /^[a-zA-Z0-9-]{1,63}$/;
const ASCII_TLD_REGEX = /^[A-Za-z]{2,63}$/;
// No hyphen: a real IDN TLD (.рф = xn--p1ai) has no ASCII part, a typo like "hotmail.co.ukķ" (xn--uk-0pa) does.
const PUNYCODE_TLD_REGEX = /^xn--[A-Za-z0-9]{2,58}$/;
const MAX_EMAIL_LENGTH = 97;
const MAX_LOCAL_PART_LENGTH = 64;
const MAX_DOMAIN_PART_LENGTH = 32;

const advancedEmailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value ?? '').trim();

  if (!value) {
    return null;
  }

  if (value.length > MAX_EMAIL_LENGTH) {
    return { email: true };
  }

  const atIndex = value.indexOf('@');
  if (atIndex === -1 || atIndex !== value.lastIndexOf('@')) {
    return { email: true };
  }

  const localPart = value.slice(0, atIndex);
  const domainPart = value.slice(atIndex + 1);

  if (
    !localPart.length ||
    localPart.length > MAX_LOCAL_PART_LENGTH ||
    !LOCAL_PART_REGEX.test(localPart) ||
    localPart.startsWith('.') ||
    localPart.endsWith('.') ||
    localPart.includes('..')
  ) {
    return { email: true };
  }

  if (!domainPart.length || domainPart.endsWith('.') || domainPart.includes('..')) {
    return { email: true };
  }

  if (domainPart.length > MAX_DOMAIN_PART_LENGTH) {
    return { email: true };
  }

  const domainLabels = domainPart.split('.');
  if (domainLabels.length < 2) {
    return { email: true };
  }

  for (const label of domainLabels) {
    if (!DOMAIN_LABEL_REGEX.test(label) || label.startsWith('-') || label.endsWith('-')) {
      return { email: true };
    }
  }

  const topLevelDomain = domainLabels[domainLabels.length - 1];
  if (!ASCII_TLD_REGEX.test(topLevelDomain) && !PUNYCODE_TLD_REGEX.test(topLevelDomain)) {
    return { email: true };
  }

  return null;
};

@Component({
  selector: 'app-funnel-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatAutocompleteModule,
  ],
  animations: [fadeAnimation],
})
export class EmailComponent implements OnInit, AfterViewInit {
  @ViewChild('emailInput') emailInput!: ElementRef;

  email = new FormControl('', [Validators.required, advancedEmailValidator]);

  // Keep translations free of markup; apply highlight markup in code.
  title =
    $localize`:@@emailTitlePrefix:Enter your email ` +
    highlightText($localize`:@@emailTitleHighlight:to get the results in one step`);
  //subtitle = 'Enter your email to complete your personal account setup';

  // Email domain suggestions - based on real production data
  commonDomains = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'icloud.com',
    'me.com',
    'aol.com',
    'outlook.com',
    'mac.com',
    'msn.com',
    'yahoo.co.uk',
    'comcast.net',
    'live.com',
    'hotmail.co.uk',
    'att.net',
    'bigpond.com',
    'yahoo.com.au',
    'googlemail.com',
    'sbcglobal.net',
    'protonmail.com',
    'btinternet.com',
    'verizon.net',
    'ymail.com', // this is not typo, it is yahoo
    'gmx.de',
    'sky.com',
    'cox.net',
    'yahoo.fr',
    'duck.com',
    'mail.com',
    'web.de',
    'bellsouth.net',
    'shaw.ca',
    'proton.me',
    'live.co.uk',
    'xtra.co.nz',
    'rogers.com',
    'qq.com',
    'rocketmail.com',
    'yahoo.com.sg',
    'pm.me',
    'yahoo.de',
    'outlook.co.nz',
    'doctors.org.uk',
    'bluewin.ch',
    'mail.ru',
  ];

  filteredDomains$: Observable<string[]>;

  isShortFlow = false; // TODO: move it to flow service and redefine there

  constructor(
    private api: ApiService,
    private flowService: FunnelFlowService,
    private userInfo: UserInfoService,
    private loadingService: LoadingService,
    private dialog: MatDialog,
    private analyticsService: AnalyticsService,
    private axonService: AxonService,
    private newsbreakService: NewsbreakService,
    @Inject(PROJECT_NAME) projectName: ProjectName
  ) {
    this.isShortFlow =
      projectName === ProjectNames.GOOGLE_FUNNEL ||
      projectName === ProjectNames.SHORT_FUNNEL ||
      projectName === ProjectNames.KINDLE_FUNNEL ||
      projectName === ProjectNames.ROMANCE_FUNNEL;

    // Set up filtered domains observable
    this.filteredDomains$ = this.email.valueChanges.pipe(
      startWith(''),
      map(value => this.filterDomains(value || ''))
    );
  }

  ngOnInit(): void {
    const email = this.userInfo.getEmail();

    if (email) {
      this.email.setValue(email);
    }
  }

  ngAfterViewInit(): void {
    this.analyticsService.track('web_login_email_view');
  }

  private filterDomains(value: string): string[] {
    // Use the first @ to handle cases where user is editing after @
    const atIndex = value.indexOf('@');

    // Only show suggestions if there's an @ and text before it
    if (atIndex === -1 || atIndex === 0) {
      return [];
    }

    const emailBeforeAt = value.substring(0, atIndex);
    const domainPart = value.substring(atIndex + 1).toLowerCase();

    // Only show suggestions if there's text before @
    if (emailBeforeAt.length === 0) {
      return [];
    }

    // Filter domains and return complete email suggestions
    return this.commonDomains
      .filter(domain => domain.toLowerCase().startsWith(domainPart))
      .map(domain => `${emailBeforeAt}@${domain}`)
      .slice(0, 8); // Limit to 8 suggestions for better UX
  }

  onSuggestionSelected(selectedEmail: string): void {
    this.email.setValue(selectedEmail);
    this.emailInput.nativeElement.focus();
  }

  displayEmail(email: string): string {
    return email;
  }

  enterPressed(): void {
    if (!this.email.value || this.email.invalid) {
      return;
    }

    this.emailInput.nativeElement.blur();

    this.loginEmail();
  }

  continueButtonClicked(): void {
    if (this.email.invalid) {
      return;
    }

    this.loginEmail();
  }

  private loginEmail() {
    this.loadingService.start();

    // Set email in the flow service before tracking events
    this.userInfo.setEmail(this.email.value!);

    // Get the device ID first, then make the API call
    const amplitudeDeviceId = this.analyticsService.getDeviceId() || '';

    this.api
      .createStripeCustomer({
        email: this.email.value!,
        amplitudeDeviceId,
        webQuizAnswers: this.flowService.getWebQuizAnswers(),
        selectedBooks: this.flowService.getSelectedBooks(),
        language: this.flowService.getLanguage(),
      })
      .pipe(
        take(1),
        tap(({ customerId }) => {
          const email = this.email.value!;
          this.flowService.handleFinishedLogin(customerId, email);

          this.analyticsService.track('web_email_entered', undefined, {
            web_email: email,
          });
          this.axonService.login(email);
          //this.newsbreakService.completeRegistration(email);
        }),
        catchError(error => {
          this.analyticsService.track('web_unexpected_error', { error });

          return this.showError();
        }),
        finalize(() => this.loadingService.finish())
      )
      .subscribe();
  }

  private showError() {
    return this.dialog
      .open<ModalComponent, ModalData>(ModalComponent, {
        data: {
          title: 'Sorry',
          message: 'Failed to Sign You In. Please try again.',
        },
      })
      .afterOpened();
  }
}
